/**
 * Litiges Stripe d'un contrat (contestation de paiement, opposition au prélèvement).
 * Renvoyés par `/pro-v2/contracts/:id/payment-details` et `/:id/payments`.
 */
export interface ContractDispute {
  id: string;
  /** Paiement contesté : même identifiant que `paymentId` des lignes de paiement. */
  chargeId: string;
  status: string;
  reason: string;
  paymentMethodType: string | null;
  /** Montant contesté, en euros. */
  amount: number;
  /** Retiré du solde du vendeur à ce jour, en euros (0 si le litige est gagné). */
  withdrawnAmount: number;
  disputedAt: string;
  evidenceDueBy: string | null;
  closedAt: string | null;
}

/** Statuts Stripe d'un litige. */
export const DISPUTE_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  warning_needs_response: { label: "Demande d'information", color: 'tw-bg-amber-100 tw-text-amber-700' },
  warning_under_review: { label: "Demande d'information en cours", color: 'tw-bg-amber-100 tw-text-amber-700' },
  warning_closed: { label: 'Demande close', color: 'tw-bg-gray-100 tw-text-gray-600' },
  needs_response: { label: 'Réponse attendue', color: 'tw-bg-orange-100 tw-text-orange-700' },
  under_review: { label: "En cours d'examen", color: 'tw-bg-amber-100 tw-text-amber-700' },
  won: { label: 'Gagné', color: 'tw-bg-green-100 tw-text-green-700' },
  lost: { label: 'Perdu', color: 'tw-bg-red-100 tw-text-red-700' },
  prevented: { label: 'Évité', color: 'tw-bg-gray-100 tw-text-gray-600' },
};

/** Motifs Stripe d'un litige. */
export const DISPUTE_REASON_LABELS: Record<string, string> = {
  bank_cannot_process: 'Refus de la banque',
  check_returned: 'Chèque retourné',
  credit_not_processed: 'Remboursement non effectué',
  customer_initiated: 'Opposition du client',
  debit_not_authorized: 'Prélèvement non autorisé',
  duplicate: 'Paiement en double',
  fraudulent: 'Paiement frauduleux',
  general: 'Autre motif',
  incorrect_account_details: 'Coordonnées bancaires incorrectes',
  insufficient_funds: 'Provision insuffisante',
  noncompliant: 'Non conforme',
  product_not_received: 'Service non reçu',
  product_unacceptable: 'Service non conforme',
  subscription_canceled: 'Abonnement résilié',
  unrecognized: 'Paiement non reconnu',
};

export const disputeStatusInfo = (status: string) =>
  DISPUTE_STATUS_LABELS[status] ?? { label: status, color: 'tw-bg-gray-100 tw-text-gray-600' };

export const disputeReasonLabel = (reason: string) => DISPUTE_REASON_LABELS[reason] ?? reason;

/** Nature du litige selon le moyen de paiement contesté. */
export const disputeKindLabel = (paymentMethodType: string | null) =>
  paymentMethodType === 'sepa_debit' ? 'Opposition au prélèvement' : 'Contestation du paiement';

/** Litige encore ouvert : le montant contesté est retenu sur le solde du vendeur. */
export const isDisputeOpen = (status: string) => ['needs_response', 'under_review'].includes(status);
